import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Bell, BellRing, Check, Copy, ExternalLink, Send, Trash2, Loader2 } from "lucide-react";
import { toast } from "sonner";
import {
  fetchAlertsStatus, saveAlertsSettings, deleteAlertsSettings,
  sendTestAlert, sendAlertSummary, fetchAlertsLog,
} from "../lib/api";

export default function AlertsPanel() {
  const [status, setStatus] = useState(null);
  const [token, setToken] = useState("");
  const [chatId, setChatId] = useState("");
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [helpOpen, setHelpOpen] = useState(false);
  const [log, setLog] = useState([]);

  useEffect(() => { refresh(); }, []);

  const refresh = async () => {
    try {
      const [s, l] = await Promise.all([fetchAlertsStatus(), fetchAlertsLog()]);
      setStatus(s);
      setLog(l.log || []);
    } catch { /* silent */ }
  };

  const save = async () => {
    if (!token || !chatId) return toast.error("Bot token and Chat ID are required");
    setSaving(true);
    try {
      const res = await saveAlertsSettings(token.trim(), chatId.trim());
      toast.success(`Connected to @${res.bot.username}`);
      setToken(""); setChatId("");
      await refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to save — check the token");
    } finally {
      setSaving(false);
    }
  };

  const disconnect = async () => {
    await deleteAlertsSettings();
    toast.success("Telegram disconnected");
    refresh();
  };

  const test = async () => {
    setTesting(true);
    try {
      await sendTestAlert();
      toast.success("Test alert sent — check your Telegram");
      refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Test failed");
    } finally { setTesting(false); }
  };

  const digest = async () => {
    try {
      const res = await sendAlertSummary();
      toast.success(`Summary sent (${res.hits_sent} breakouts)`);
      refresh();
    } catch (e) { toast.error(e?.response?.data?.detail || "Digest failed"); }
  };

  return (
    <section id="alerts" className="relative py-16 lg:py-24 border-t border-white/8" data-testid="alerts-section">
      <div className="mx-auto max-w-[1400px] px-6 lg:px-10">
        <div className="max-w-3xl mb-10">
          <div className="text-[10px] font-mono tracking-widest text-[#FFB020] uppercase">03 · Real-time alerts</div>
          <h2 className="mt-2 font-display text-4xl lg:text-5xl font-semibold">
            Get pinged the <span className="text-[#FFB020]">moment</span> a breakout fires.
          </h2>
          <p className="mt-3 text-sm text-white/55 max-w-2xl">
            Connect a Telegram bot and PsychLevel will DM you every time a fresh 1H candle closes above
            a psychological level — during NSE hours we check every 15 minutes.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left: connection card */}
          <div className="lg:col-span-2 card-surface rounded-2xl p-6">
            <div className="flex items-center gap-3 mb-5">
              <div className={`h-9 w-9 rounded-lg border flex items-center justify-center ${status?.configured ? "bg-[#39FF14]/12 border-[#39FF14]/30" : "bg-white/5 border-white/10"}`}>
                {status?.configured ? <BellRing className="h-4 w-4 text-[#39FF14]" /> : <Bell className="h-4 w-4 text-white/50" />}
              </div>
              <div className="flex-1">
                <div className="font-display font-medium">
                  {status?.configured ? "Telegram connected" : "Telegram not connected"}
                </div>
                <div className="font-mono text-xs text-white/45">
                  {status?.configured ? (
                    <>bot: <span className="text-white/70">@{status?.bot?.username || "—"}</span> · sent: {status?.sent_count} · source: {status?.source}</>
                  ) : (
                    <>Paste your bot token & chat ID below to enable alerts.</>
                  )}
                </div>
              </div>
              {status?.configured && (
                <button onClick={disconnect} className="pill-btn text-xs inline-flex items-center gap-2" data-testid="alerts-disconnect-btn">
                  <Trash2 className="h-3.5 w-3.5" /> Disconnect
                </button>
              )}
            </div>

            {!status?.configured && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div>
                  <label className="text-[10px] font-mono tracking-widest text-white/45 uppercase">Bot Token</label>
                  <input
                    data-testid="alerts-token-input"
                    value={token}
                    onChange={(e) => setToken(e.target.value)}
                    placeholder="1234567890:AAH..."
                    className="mt-2 w-full px-3 py-2.5 rounded-lg bg-white/5 border border-white/10 focus:border-[#FFB020]/50 focus:outline-none text-sm text-white placeholder:text-white/30 font-mono"
                  />
                </div>
                <div>
                  <label className="text-[10px] font-mono tracking-widest text-white/45 uppercase">Chat ID</label>
                  <input
                    data-testid="alerts-chatid-input"
                    value={chatId}
                    onChange={(e) => setChatId(e.target.value)}
                    placeholder="e.g. 987654321"
                    className="mt-2 w-full px-3 py-2.5 rounded-lg bg-white/5 border border-white/10 focus:border-[#FFB020]/50 focus:outline-none text-sm text-white placeholder:text-white/30 font-mono"
                  />
                </div>
              </div>
            )}

            <div className="mt-5 flex flex-wrap items-center gap-3">
              {!status?.configured ? (
                <button data-testid="alerts-connect-btn" onClick={save} disabled={saving} className="pill-btn-primary inline-flex items-center gap-2 text-sm disabled:opacity-50">
                  {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />}
                  {saving ? "Verifying…" : "Connect Telegram"}
                </button>
              ) : (
                <>
                  <button data-testid="alerts-test-btn" onClick={test} disabled={testing} className="pill-btn-primary inline-flex items-center gap-2 text-sm disabled:opacity-50">
                    {testing ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                    {testing ? "Sending…" : "Send test message"}
                  </button>
                  <button data-testid="alerts-digest-btn" onClick={digest} className="pill-btn text-sm inline-flex items-center gap-2">
                    <Send className="h-4 w-4" /> Push current digest
                  </button>
                </>
              )}
              <button onClick={() => setHelpOpen((v) => !v)} className="pill-btn text-sm inline-flex items-center gap-2" data-testid="alerts-help-toggle">
                <ExternalLink className="h-4 w-4" /> {helpOpen ? "Hide setup steps" : "How do I get these?"}
              </button>
            </div>

            <AnimatePresence>
              {helpOpen && (
                <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: "auto" }} exit={{ opacity: 0, height: 0 }} className="overflow-hidden">
                  <div className="mt-6 rounded-xl border border-white/8 bg-white/2 p-5 space-y-4">
                    <SetupStep n={1} title="Create the bot">
                      Open Telegram → search <span className="ticker-badge">@BotFather</span> → send <Cmd>/newbot</Cmd> → pick a name & username.
                      BotFather will reply with your <span className="text-white">Bot Token</span> (e.g. <span className="font-mono text-[#00E5FF]">7412345678:AAH...</span>). Copy it.
                    </SetupStep>
                    <SetupStep n={2} title="Start your bot">
                      Open the link BotFather gave you (or search your bot&apos;s username) → hit <Cmd>Start</Cmd>. Send any message like &ldquo;hi&rdquo;.
                    </SetupStep>
                    <SetupStep n={3} title="Get your Chat ID">
                      In a browser, open <span className="font-mono text-[#00E5FF] break-all">https://api.telegram.org/bot&lt;YOUR_TOKEN&gt;/getUpdates</span>{" "}
                      — look for <span className="font-mono">&quot;chat&quot;:&#123;&quot;id&quot;: <span className="text-[#39FF14]">987654321</span>&#125;</span>. That number is your Chat ID.
                      <button onClick={() => { navigator.clipboard.writeText("https://api.telegram.org/bot<TOKEN>/getUpdates"); toast.success("URL template copied"); }} className="ml-2 pill-btn text-xs inline-flex items-center gap-1"><Copy className="h-3 w-3" /> Copy URL</button>
                    </SetupStep>
                    <SetupStep n={4} title="Paste & connect">
                      Drop both values above and hit <span className="text-white">Connect Telegram</span>. We&apos;ll verify the token by calling <Cmd>getMe</Cmd>.
                    </SetupStep>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          {/* Right: log */}
          <div className="card-surface rounded-2xl p-6" data-testid="alerts-log-panel">
            <h3 className="font-display text-lg font-medium mb-4">Recent alerts</h3>
            {log.length === 0 && <div className="text-sm text-white/45">No alerts sent yet.</div>}
            <div className="space-y-2 max-h-[380px] overflow-y-auto">
              {log.map((l, i) => (
                <div key={i} className="flex items-center justify-between rounded-lg border border-white/6 px-3 py-2">
                  <div className="flex items-center gap-2">
                    <span className="ticker-badge">{l.symbol}</span>
                    <span className="font-mono text-xs text-[#00E5FF]">₹{l.level}</span>
                  </div>
                  <span className="font-mono text-[10px] text-white/45">
                    {l.sent_at ? new Date(l.sent_at).toLocaleString("en-IN", { timeZone: "Asia/Kolkata", month: "short", day: "2-digit", hour: "2-digit", minute: "2-digit", hour12: false }) : ""}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function SetupStep({ n, title, children }) {
  return (
    <div className="flex gap-4">
      <div className="h-7 w-7 shrink-0 rounded-md bg-white/6 border border-white/10 flex items-center justify-center font-mono text-xs text-[#FFB020]">
        {n}
      </div>
      <div>
        <div className="font-display font-medium text-sm mb-1">{title}</div>
        <div className="text-sm text-white/65 leading-relaxed">{children}</div>
      </div>
    </div>
  );
}

function Cmd({ children }) {
  return <span className="font-mono text-xs px-1.5 py-0.5 rounded bg-white/5 border border-white/10 text-[#39FF14]">{children}</span>;
}
